/**
 * OpenExternalConfirm — modal that asks the user whether to open an external
 * URL that an HTML preview iframe requested.
 *
 * Part of Phase 2 exfil-hole mitigation
 * (docs/plans/2026-05-21_PREVIEW_SANDBOX.md). The sandboxed iframe can
 * still ask the parent to open URLs via `papan:openExternal` — friction
 * via this dialog blocks silent exfil while allowing legitimate
 * `<a target="_blank">` clicks once the user confirms.
 *
 * Default focus is on the safer choice (Cancel). The URL is displayed
 * unmasked so the user can spot suspicious patterns (long query strings,
 * unfamiliar domains, encoded payloads).
 */
import { useEffect, useRef } from 'react'
import { ExternalLink, X } from 'lucide-react'
import { createPortal } from 'react-dom'
import { useTheme, getThemeClass } from '@papan/core-ui/ui'

interface OpenExternalConfirmProps {
  url: string
  onConfirm: () => void
  onCancel: () => void
}

export function OpenExternalConfirm({ url, onConfirm, onCancel }: OpenExternalConfirmProps) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)

  const cancelBtnRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    cancelBtnRef.current?.focus()
  }, [])

  // Try to surface the host for at-a-glance recognition.
  let host = ''
  try {
    host = new URL(url).host
  } catch {
    host = url.slice(0, 40)
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />

      <div className={`${themeClass} relative themed-bg-secondary rounded-lg shadow-2xl w-[440px] max-w-[90vw] border themed-border`}>
        <div className="flex items-center justify-between px-4 py-3 border-b themed-border">
          <div className="flex items-center gap-2">
            <ExternalLink size={16} className="themed-text-secondary" />
            <h3 className="text-sm font-medium themed-text">Open external link?</h3>
          </div>
          <button onClick={onCancel} className="themed-text-muted hover:themed-text">
            <X size={16} />
          </button>
        </div>

        <div className="px-4 py-3 space-y-2">
          <div className="text-sm themed-text-secondary">
            This preview wants to open a link on <span className="font-medium themed-text">{host}</span>.
          </div>
          <div className="text-xs themed-text-muted font-mono break-all max-h-32 overflow-y-auto px-2 py-1.5 rounded themed-bg-tertiary border themed-border">
            {url}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t themed-border">
          <button
            ref={cancelBtnRef}
            onClick={onCancel}
            className="px-3 py-1.5 text-sm themed-text-secondary hover:themed-text rounded transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-3 py-1.5 text-sm themed-btn-primary rounded transition-colors"
          >
            Open
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
